import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { TEACHER } from "../constants/testIds";
import { Phone, CheckCircle2, XCircle, Clock, BarChart3, Download, Megaphone, MessageSquare, CalendarX, ClipboardCheck, AlarmClock } from "lucide-react";
import MessagesPage from "./MessagesPage";
import { AttachmentList } from "../components/Attachments";
import { TeacherHomework } from "../components/Homework";
import { ClassNotes } from "../components/ClassNotes";
import { GeoCheckIn } from "../components/GeoCheckIn";
import { TeacherConferences } from "../components/Conferences";

export default function TeacherDashboard() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [classes, setClasses] = React.useState([]);
    const [classId, setClassId] = React.useState("");
    const [roster, setRoster] = React.useState({ class: null, students: [] });

    React.useEffect(() => {
        api.get("/teacher/classes").then(r => {
            setClasses(r.data);
            if (r.data.length > 0 && !classId) setClassId(r.data[0].id);
        });
    }, []);

    React.useEffect(() => {
        if (!classId) return;
        api.get(`/classes/${classId}/roster`).then(r => setRoster(r.data));
    }, [classId]);

    return (
        <main className="py-12 min-h-[calc(100vh-5rem)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex items-baseline justify-between mb-8 flex-wrap gap-4">
                    <div>
                        <div className="overline text-brand-stone">Teacher</div>
                        <h1 className="font-serif text-4xl text-brand-ink">As-salamu alaykum, {user?.full_name?.split(" ")[0]}</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <SelfPresentButton />
                        <RunningLateButton />
                        <CallInAbsentButton />
                        <Select value={classId} onValueChange={setClassId}>
                            <SelectTrigger data-testid={TEACHER.classSelect} className="w-64"><SelectValue placeholder="Select class" /></SelectTrigger>
                            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mb-6"><GeoCheckIn /></div>
                <Tabs defaultValue="classroom">
                    <TabsList className="mb-6 flex-wrap h-auto">
                        <TabsTrigger value="classroom">Classroom</TabsTrigger>
                        <TabsTrigger value="myatt" data-testid="teacher-my-attendance-tab">My attendance</TabsTrigger>
                        <TabsTrigger value="conf" data-testid="teacher-conferences-tab">Conferences</TabsTrigger>
                        <TabsTrigger value="ann" data-testid="teacher-announcements-tab">Announcements</TabsTrigger>
                        <TabsTrigger value="msg" data-testid="teacher-messages-tab">Messages</TabsTrigger>
                    </TabsList>
                    <TabsContent value="classroom">
                        {classes.length === 0 && (
                            <Card><CardContent className="p-10 text-center text-brand-stone">{t("teacher_no_class")}</CardContent></Card>
                        )}
                        {roster.class && (
                            <Tabs defaultValue="roster">
                                <TabsList className="mb-6 flex-wrap h-auto">
                                    <TabsTrigger value="roster">{t("teacher_roster")}</TabsTrigger>
                                    <TabsTrigger value="attend" data-testid={TEACHER.attendanceTab}>{t("tab_attendance")}</TabsTrigger>
                                    <TabsTrigger value="report" data-testid="teacher-attendance-report-tab">{t("attendance_report")}</TabsTrigger>
                                    <TabsTrigger value="grades" data-testid={TEACHER.gradesTab}>{t("tab_report_cards")}</TabsTrigger>
                                    <TabsTrigger value="hw" data-testid="teacher-homework-tab">Homework</TabsTrigger>
                                    <TabsTrigger value="notes" data-testid="teacher-notes-tab">Substitute notes</TabsTrigger>
                                </TabsList>
                                <TabsContent value="roster"><Roster students={roster.students} /></TabsContent>
                                <TabsContent value="attend">
                                    {classes.length > 1
                                        ? <MultiClassAttendance classes={classes} />
                                        : <AttendanceForm classId={roster.class.id} students={roster.students} />}
                                </TabsContent>
                                <TabsContent value="report"><AttendanceReport classId={roster.class.id} /></TabsContent>
                                <TabsContent value="grades"><GradesForm classId={roster.class.id} students={roster.students} /></TabsContent>
                                <TabsContent value="hw"><TeacherHomework classId={roster.class.id} /></TabsContent>
                                <TabsContent value="notes"><ClassNotes classId={roster.class.id} /></TabsContent>
                            </Tabs>
                        )}
                    </TabsContent>
                    <TabsContent value="ann"><TeacherAnnouncements /></TabsContent>
                    <TabsContent value="msg"><MessagesPage /></TabsContent>
                    <TabsContent value="myatt"><MyAttendance /></TabsContent>
                    <TabsContent value="conf"><TeacherConferences classes={classes} /></TabsContent>
                </Tabs>
            </div>
        </main>
    );
}

function TeacherAnnouncements() {
    const [items, setItems] = React.useState([]);
    React.useEffect(() => { api.get("/announcements").then(r => setItems(r.data)); }, []);
    return (
        <div className="space-y-3">
            {items.length === 0 && <div className="text-brand-stone text-center py-16">No announcements yet.</div>}
            {items.map(a => (
                <Card key={a.id}><CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <Megaphone className="w-4 h-4 text-primary" />
                        <div className="overline text-brand-stone">{new Date(a.created_at).toLocaleDateString()}</div>
                    </div>
                    <h3 className="font-dash text-lg font-semibold">{a.title}</h3>
                    <p className="text-brand-stone mt-1 whitespace-pre-wrap">{a.body}</p>
                    <AttachmentList items={a.attachments} testid={`teacher-ann-att-${a.id}`} />
                </CardContent></Card>
            ))}
        </div>
    );
}

function Roster({ students }) {
    return (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={TEACHER.rosterTable}>
            {students.map((s, i) => (
                <Card key={s.id} className="card-lift" style={{ animationDelay: `${i * 40}ms` }}>
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center font-dash font-semibold">
                                {s.first_name[0]}{s.last_name[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-dash text-lg font-semibold">{s.first_name} {s.last_name}</div>
                            </div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-border/60 space-y-2 text-sm">
                            <div>
                                <div className="overline text-brand-stone text-[10px]">Parent</div>
                                <div className="text-brand-ink font-medium">{s.parent_name || "—"}</div>
                            </div>
                            {s.parent_phone ? (
                                <a
                                    href={`tel:${s.parent_phone}`}
                                    className="flex items-center gap-2 text-primary hover:underline"
                                >
                                    <Phone className="w-4 h-4" /> {s.parent_phone}
                                </a>
                            ) : (
                                <div className="flex items-center gap-2 text-brand-stone">
                                    <Phone className="w-4 h-4" /> No phone on file
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            ))}
            {students.length === 0 && <div className="col-span-full text-brand-stone text-center py-16">No students enrolled in this class yet.</div>}
        </div>
    );
}

function MultiClassAttendance({ classes }) {
    const [date, setDate] = React.useState(new Date().toISOString().slice(0, 10));
    const [rosters, setRosters] = React.useState([]);
    const [savingAll, setSavingAll] = React.useState(false);
    const savers = React.useRef({});
    React.useEffect(() => {
        Promise.all(classes.map(c => api.get(`/classes/${c.id}/roster`).then(r => r.data)))
            .then(setRosters);
    }, [classes]);
    async function saveAll() {
        setSavingAll(true);
        const results = await Promise.allSettled(Object.values(savers.current).map(fn => fn(true)));
        const failed = results.filter(r => r.status === "rejected").length;
        if (failed) toast.error(`${failed} class${failed === 1 ? "" : "es"} failed to save`);
        else toast.success(`Attendance saved for all ${results.length} classes`);
        setSavingAll(false);
    }
    return (
        <div className="space-y-6" data-testid="multi-class-attendance">
            <div className="flex items-end gap-3 flex-wrap">
                <div>
                    <Label>Date (all your classes)</Label>
                    <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-48 mt-1.5" data-testid="multi-attendance-date" />
                </div>
                <p className="text-xs text-brand-stone pb-2 flex-1">Showing all {classes.length} of your classes.</p>
                <Button onClick={saveAll} disabled={savingAll || rosters.length === 0} className="bg-primary hover:bg-primary/90" data-testid="save-all-attendance">
                    {savingAll ? "Saving…" : "Save all classes"}
                </Button>
            </div>
            {rosters.map(r => (
                <AttendanceForm key={r.class.id} classId={r.class.id} students={r.students} date={date} title={r.class.name}
                    register={fn => { if (fn) savers.current[r.class.id] = fn; else delete savers.current[r.class.id]; }} />
            ))}
        </div>
    );
}

function AttendanceForm({ classId, students, date: sharedDate, title, register }) {
    const [ownDate, setDate] = React.useState(new Date().toISOString().slice(0, 10));
    const date = sharedDate || ownDate;
    const [statuses, setStatuses] = React.useState({});
    const [existing, setExisting] = React.useState(false);

    // Load any saved attendance for the picked date so teachers can edit history.
    React.useEffect(() => {
        if (!classId || !date) return;
        (async () => {
            try {
                const { data } = await api.get("/attendance", { params: { class_id: classId, date } });
                const rec = data[0];
                if (rec) {
                    const map = Object.fromEntries(rec.records.map(r => [r.student_id, r.status]));
                    setStatuses(Object.fromEntries(students.map(s => [s.id, map[s.id] || "present"])));
                    setExisting(true);
                } else {
                    setStatuses(Object.fromEntries(students.map(s => [s.id, "present"])));
                    setExisting(false);
                }
            } catch {
                setStatuses(Object.fromEntries(students.map(s => [s.id, "present"])));
                setExisting(false);
            }
        })();
    }, [classId, date, students]);

    async function save(silent = false) {
        const records = Object.entries(statuses).map(([student_id, status]) => ({ student_id, status }));
        await api.post("/attendance", { class_id: classId, date, records });
        if (!silent) toast.success(`${title ? `${title}: ` : ""}${existing ? "Attendance updated" : "Attendance saved"}`);
        setExisting(true);
    }
    React.useEffect(() => {
        if (!register) { return undefined; }
        register(save);
        return () => { register(null); };
    });
    const ICON = { present: CheckCircle2, absent: XCircle, late: Clock, excused: Clock };
    return (
        <Card data-testid={title ? `class-attendance-${classId}` : undefined}><CardContent className="p-6">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                {title ? (
                    <div className="flex items-center gap-3">
                        <h3 className="font-dash text-lg font-semibold">{title}</h3>
                        <Badge variant="secondary">{students.length} students</Badge>
                        {existing && <Badge className="bg-emerald-100 text-emerald-800 border-0">Saved · editing</Badge>}
                    </div>
                ) : (
                <div>
                    <Label>Date</Label>
                    <div className="flex items-center gap-3 mt-1.5">
                        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-48" />
                        {existing && <Badge className="bg-emerald-100 text-emerald-800 border-0">Saved · editing</Badge>}
                    </div>
                </div>
                )}
                <Button data-testid={title ? `save-attendance-${classId}` : TEACHER.saveAttendanceBtn} onClick={() => save()} className="bg-primary hover:bg-primary/90">
                    {existing ? "Update attendance" : "Save attendance"}
                </Button>
            </div>
            <div className="space-y-2">
                {students.map(s => {
                    const Icon = ICON[statuses[s.id]] || CheckCircle2;
                    return (
                        <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                            <div className="flex items-center gap-3">
                                <Icon className={`w-4 h-4 ${statuses[s.id] === "present" ? "text-emerald-600" : statuses[s.id] === "absent" ? "text-destructive" : "text-amber-600"}`} />
                                <span>{s.first_name} {s.last_name}</span>
                            </div>
                            <Select value={statuses[s.id]} onValueChange={v => setStatuses({ ...statuses, [s.id]: v })}>
                                <SelectTrigger className="w-36" data-testid={`att-status-${classId}-${s.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="present">Present</SelectItem>
                                    <SelectItem value="absent">Absent</SelectItem>
                                    <SelectItem value="late">Late</SelectItem>
                                    <SelectItem value="excused">Excused</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    );
                })}
                {students.length === 0 && <div className="text-brand-stone text-center py-12">No students to mark.</div>}
            </div>
        </CardContent></Card>
    );
}

function AttendanceReport({ classId }) {
    const [summary, setSummary] = React.useState(null);
    const [downloading, setDownloading] = React.useState(false);

    React.useEffect(() => {
        if (!classId) return;
        api.get(`/attendance/summary/${classId}`).then(r => setSummary(r.data)).catch(() => setSummary(null));
    }, [classId]);

    async function downloadPdf() {
        setDownloading(true);
        try {
            const res = await api.get(`/attendance/summary/${classId}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const cd = res.headers?.["content-disposition"] || "";
            const match = /filename="([^"]+)"/.exec(cd);
            const link = document.createElement("a");
            link.href = url;
            link.download = match?.[1] || "attendance.pdf";
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("PDF downloaded");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not build PDF");
        } finally {
            setDownloading(false);
        }
    }

    if (!summary) return <Card><CardContent className="p-10 text-center text-brand-stone">Loading report…</CardContent></Card>;

    const cls = summary.rows;
    const hasStudents = cls.length > 0;
    const overall = hasStudents
        ? Math.round(cls.reduce((a, r) => a + r.percent, 0) / cls.length)
        : 0;

    return (
        <Card data-testid="teacher-attendance-report">
            <CardContent className="p-6">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <div className="overline text-brand-stone">Attendance report</div>
                        <div className="font-serif text-2xl text-brand-ink mt-1">
                            {hasStudents
                                ? `${summary.sessions_recorded} session${summary.sessions_recorded === 1 ? "" : "s"} recorded`
                                : "No students enrolled yet"}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {hasStudents && (
                            <div className="flex items-center gap-2 text-primary">
                                <BarChart3 className="w-4 h-4" />
                                <div className="font-dash text-xl font-semibold">{overall}% class average</div>
                            </div>
                        )}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={downloadPdf}
                            disabled={downloading}
                            data-testid="download-attendance-pdf-btn"
                            className="gap-2 border-primary/30 text-primary hover:bg-primary/5">
                            <Download className="w-4 h-4" /> {downloading ? "Building PDF…" : "Download PDF"}
                        </Button>
                    </div>
                </div>
                {cls.length === 0 ? (
                    <div className="text-brand-stone text-center py-12">No students to report on yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-brand-stone border-b border-border">
                                    <th className="pb-3 pr-4">Student</th>
                                    <th className="pb-3 px-3">Present</th>
                                    <th className="pb-3 px-3">Absent</th>
                                    <th className="pb-3 px-3">Late</th>
                                    <th className="pb-3 px-3">Excused</th>
                                    <th className="pb-3 px-3">Sessions</th>
                                    <th className="pb-3 pl-3">Attendance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cls.map(r => (
                                    <tr key={r.student_id} className="border-b border-border/60">
                                        <td className="py-3 pr-4 font-medium">{r.name}</td>
                                        <td className="py-3 px-3 text-emerald-700">{r.present}</td>
                                        <td className="py-3 px-3 text-destructive">{r.absent}</td>
                                        <td className="py-3 px-3 text-amber-700">{r.late}</td>
                                        <td className="py-3 px-3 text-brand-stone">{r.excused}</td>
                                        <td className="py-3 px-3">{r.total}</td>
                                        <td className="py-3 pl-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-24 h-2 rounded-full bg-border overflow-hidden">
                                                    <div
                                                        className={`h-full ${r.percent >= 90 ? "bg-emerald-500" : r.percent >= 75 ? "bg-amber-500" : "bg-destructive"}`}
                                                        style={{ width: `${r.percent}%` }}
                                                    />
                                                </div>
                                                <span className="font-dash font-semibold w-10">{r.percent}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function GradesForm({ classId, students }) {
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({
        student_id: "", term: "Fall 2026",
        grades: { Quran: "A", Islamic_Studies: "A", Arabic: "A", Character: "A" },
        comments: "",
    });
    async function save() {
        await api.post("/report-cards", { ...form, class_id: classId });
        toast.success("Report card saved");
        setOpen(false);
    }
    return (
        <Card><CardContent className="p-6">
            <div className="mb-6"><Button data-testid={TEACHER.saveReportCardBtn} onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90">Issue report card</Button></div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Issue report card</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Student</Label>
                            <Select value={form.student_id} onValueChange={v => setForm({ ...form, student_id: v })}>
                                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select student" /></SelectTrigger>
                                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div><Label>Term</Label><Input value={form.term} onChange={e => setForm({ ...form, term: e.target.value })} className="mt-1.5" /></div>
                        {Object.keys(form.grades).map(subj => (
                            <div key={subj}>
                                <Label>{subj.replace("_", " ")}</Label>
                                <Select value={form.grades[subj]} onValueChange={v => setForm({ ...form, grades: { ...form.grades, [subj]: v } })}>
                                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                    <SelectContent>{["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D", "F"].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        ))}
                        <div><Label>Teacher comments</Label><Textarea value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} className="mt-1.5" /></div>
                        <Button onClick={save} className="w-full bg-primary hover:bg-primary/90" disabled={!form.student_id}>Save report card</Button>
                    </div>
                </DialogContent>
            </Dialog>
            <p className="text-sm text-brand-stone">Issue term report cards for any student in this class. They&apos;ll appear in the parent&apos;s dashboard immediately.</p>
        </CardContent></Card>
    );
}


function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function SelfPresentButton() {
    const [busy, setBusy] = React.useState(false);
    const [done, setDone] = React.useState(false);
    async function tap() {
        setBusy(true);
        try {
            await api.post("/teacher/self-present", { date: todayIso() });
            toast.success("Marked yourself present for today");
            setDone(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't mark present");
        } finally { setBusy(false); }
    }
    return (
        <Button
            onClick={tap}
            disabled={busy || done}
            data-testid="self-present-btn"
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-70"
        >
            <CheckCircle2 className="w-4 h-4" /> {done ? "You're marked present" : (busy ? "Marking…" : "I'm here")}
        </Button>
    );
}

function RunningLateButton() {
    const [open, setOpen] = React.useState(false);
    const [date, setDate] = React.useState(todayIso());
    const [minutes, setMinutes] = React.useState("");
    const [note, setNote] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    async function submit() {
        const n = parseInt(minutes, 10);
        if (!Number.isFinite(n) || n < 1 || n > 240) {
            return toast.error("Enter minutes between 1 and 240");
        }
        setBusy(true);
        try {
            await api.post("/teacher/self-late", { date, minutes_late: n, note: note || null });
            toast.success(`Admin notified — you'll be ${n} min late`);
            setOpen(false); setMinutes(""); setNote("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to report");
        } finally { setBusy(false); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                data-testid="running-late-btn"
                className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50"
            >
                <AlarmClock className="w-4 h-4" /> Running late
            </Button>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-serif text-2xl">Running late</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="text-sm text-brand-stone">
                        Let admin know you&apos;ll be late so they can cover the class opening.
                    </div>
                    <div>
                        <Label>Session date</Label>
                        <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="self-late-date" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>How many minutes late?</Label>
                        <Input
                            type="number"
                            min={1}
                            max={240}
                            placeholder="e.g. 15"
                            value={minutes}
                            onChange={e => setMinutes(e.target.value)}
                            data-testid="self-late-minutes"
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label>Reason (optional)</Label>
                        <Textarea data-testid="self-late-note" value={note} onChange={e => setNote(e.target.value)} rows={2} className="mt-1.5" placeholder="e.g. Traffic on I-5" />
                    </div>
                    <Button onClick={submit} disabled={busy} data-testid="submit-self-late-btn" className="w-full bg-primary hover:bg-primary/90">
                        {busy ? "Reporting…" : "Notify admin"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function CallInAbsentButton() {
    const [open, setOpen] = React.useState(false);
    const [date, setDate] = React.useState(todayIso());
    const [note, setNote] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    async function submit() {
        setBusy(true);
        try {
            await api.post("/teacher/self-absence", { date, note: note || null });
            toast.success("Absence reported to admin");
            setOpen(false); setNote("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to report");
        } finally { setBusy(false); }
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                data-testid="call-in-absent-btn"
                className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
            >
                <CalendarX className="w-4 h-4" /> Call in absent
            </Button>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-serif text-2xl">Call in absent</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="text-sm text-brand-stone">
                        Notify admin that you cannot make it. They&apos;ll arrange a substitute.
                    </div>
                    <div>
                        <Label>Session date</Label>
                        <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="self-absence-date" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Reason (optional)</Label>
                        <Textarea data-testid="self-absence-note" value={note} onChange={e => setNote(e.target.value)} rows={3} className="mt-1.5" placeholder="e.g. Family emergency, unwell…" />
                    </div>
                    <Button onClick={submit} disabled={busy} data-testid="submit-self-absence-btn" className="w-full bg-primary hover:bg-primary/90">
                        {busy ? "Reporting…" : "Report absence"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function MyAttendance() {
    const [rows, setRows] = React.useState([]);
    React.useEffect(() => {
        api.get("/teacher/my-attendance").then(r => setRows(r.data)).catch(() => setRows([]));
    }, []);
    const STATUS_CLS = {
        present: "bg-emerald-100 text-emerald-800",
        absent: "bg-rose-100 text-rose-800",
        late: "bg-amber-100 text-amber-800",
    };
    return (
        <Card><CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
                <ClipboardCheck className="w-4 h-4 text-primary" />
                <h2 className="font-dash text-xl font-semibold">My attendance history</h2>
            </div>
            {rows.length === 0 ? (
                <div className="text-brand-stone italic text-sm py-6 text-center">No attendance recorded yet.</div>
            ) : (
                <div className="space-y-2">
                    {rows.map(r => (
                        <div key={r.date + r.teacher_id} className="flex items-center gap-3 border border-border rounded-lg px-4 py-2.5 text-sm">
                            <Badge className={`${STATUS_CLS[r.status] || "bg-secondary"} border-0 capitalize`}>{r.status}</Badge>
                            <div className="font-medium">{r.date}</div>
                            {r.self_reported && <Badge variant="secondary" className="text-[10px]">Self-reported</Badge>}
                            {r.note && <span className="text-brand-stone italic ml-2">— &quot;{r.note}&quot;</span>}
                        </div>
                    ))}
                </div>
            )}
        </CardContent></Card>
    );
}
